import axios from "axios";
import moment from "moment";
import { API_R3API_ODOO_EXECUTE_URI } from "../Context/Constants";
import { getAuthRequestContext } from "./authRequest";

/**
 * Recommended Lambda / execute response for useLot (JSON body):
 *
 * Success (navigate to View API):
 *   HTTP 200 + { "return_code": 0, "status": "SUCCESS", "message": "..." }
 *
 * No tokens (stay on generator, show message):
 *   HTTP 400 + { "return_code": 1, "status": "TOKENS_UNAVAILABLE", "message": "..." }
 *   — or HTTP 200 + same JSON if you cannot use 400
 */

export const USE_LOT_ERROR_CODES = {
  TOKENS_UNAVAILABLE: "TOKENS_UNAVAILABLE",
  ERROR: "ERROR"
};

const TOKENS_UNAVAILABLE_STATUS_TOKENS = new Set([
  "tokens_unavailable",
  "tokens_not_available",
  "quantity_not_available",
  "no_tokens"
]);

const normalizeStatusToken = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const isTokensUnavailableMessage = (message) =>
  /tokens not available|quantity not available|sorry,\s*tokens/i.test(String(message ?? ""));

const coerceResponsePayload = (data) => {
  if (data == null || data === "") {
    return data;
  }
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return data;
      }
    }
    return data;
  }
  return data;
};

/** Unwrap API Gateway / Lambda proxy envelopes ({ statusCode, body }). */
export const unwrapUseLotEnvelope = (data) => {
  let current = coerceResponsePayload(data);

  for (let depth = 0; depth < 3; depth += 1) {
    if (current == null || typeof current !== "object") {
      break;
    }

    const nestedBody = current.body;
    const hasNestedBody = nestedBody != null && nestedBody !== "";
    const topReturnCode = current.return_code ?? current.returnCode;
    const topStatus = current.status ?? current.error_code;

    if (hasNestedBody && topReturnCode == null && topStatus == null) {
      current = coerceResponsePayload(nestedBody);
      continue;
    }

    if (hasNestedBody && typeof nestedBody === "string") {
      const inner = coerceResponsePayload(nestedBody);
      if (inner !== nestedBody) {
        current = inner;
        continue;
      }
    }

    break;
  }

  return current;
};

const readReturnCode = (payload) => {
  if (payload == null || typeof payload !== "object") {
    return null;
  }
  const raw = payload.return_code ?? payload.returnCode;
  if (raw == null || raw === "") {
    return null;
  }
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
};

/**
 * @param {unknown} data
 * @param {{ httpOk?: boolean }} [options]
 */
export const parseUseLotResponse = (data, options = {}) => {
  const { httpOk = false } = options;
  const payload = unwrapUseLotEnvelope(data);

  if (payload == null || payload === "") {
    if (httpOk) {
      return { ok: true, code: "SUCCESS", message: null };
    }
    return {
      ok: false,
      code: USE_LOT_ERROR_CODES.ERROR,
      message: "Empty response from server."
    };
  }

  if (typeof payload === "string") {
    const message = payload.trim();
    if (!message && httpOk) {
      return { ok: true, code: "SUCCESS", message: null };
    }
    if (isTokensUnavailableMessage(message)) {
      return {
        ok: false,
        code: USE_LOT_ERROR_CODES.TOKENS_UNAVAILABLE,
        message: message || "Sorry, tokens not available in lots"
      };
    }
    if (httpOk) {
      return { ok: true, code: "SUCCESS", message: message || null };
    }
    return {
      ok: false,
      code: USE_LOT_ERROR_CODES.ERROR,
      message: message || "Unable to consume token."
    };
  }

  if (typeof payload !== "object") {
    if (httpOk) {
      return { ok: true, code: "SUCCESS", message: null };
    }
    return {
      ok: false,
      code: USE_LOT_ERROR_CODES.ERROR,
      message: "Unexpected response from server."
    };
  }

  const returnCode = readReturnCode(payload);
  const statusRaw = normalizeStatusToken(payload.status ?? payload.error_code);
  const message =
    payload.message ??
    payload.msg ??
    payload.error ??
    "";

  if (returnCode === 0) {
    return { ok: true, code: "SUCCESS", message: message || null };
  }

  if (isTokensUnavailableMessage(message) || TOKENS_UNAVAILABLE_STATUS_TOKENS.has(statusRaw)) {
    return {
      ok: false,
      code: USE_LOT_ERROR_CODES.TOKENS_UNAVAILABLE,
      message: message || "Sorry, tokens not available in lots"
    };
  }

  if (returnCode != null && returnCode !== 0) {
    return {
      ok: false,
      code: USE_LOT_ERROR_CODES.ERROR,
      message: message || "Unable to consume token."
    };
  }

  if (statusRaw === "success" || statusRaw === "ok") {
    return { ok: true, code: "SUCCESS", message: message || null };
  }

  if (statusRaw === "error" || statusRaw === "failed" || statusRaw === "failure") {
    return {
      ok: false,
      code: USE_LOT_ERROR_CODES.ERROR,
      message: message || "Unable to consume token."
    };
  }

  if (httpOk && returnCode == null && !statusRaw && !message) {
    return { ok: true, code: "SUCCESS", message: null };
  }

  return {
    ok: false,
    code: USE_LOT_ERROR_CODES.ERROR,
    message: message || "Unable to consume token."
  };
};

const throwUseLotFailure = (parsed) => {
  const error = new Error(parsed.message);
  error.code = parsed.code;
  throw error;
};

export const parseUseLotHttpError = (axiosError) => {
  const status = axiosError?.response?.status;
  const data = axiosError?.response?.data ?? axiosError?.message;
  const parsed = parseUseLotResponse(data, { httpOk: status != null && status >= 200 && status < 300 });

  if (!parsed.ok) {
    return parsed;
  }

  if (status === 400) {
    return {
      ok: false,
      code: USE_LOT_ERROR_CODES.TOKENS_UNAVAILABLE,
      message:
        (typeof data === "object" && data?.message) ||
        (typeof data === "string" ? data : "") ||
        "Sorry, tokens not available in lots"
    };
  }

  return {
    ok: false,
    code: USE_LOT_ERROR_CODES.ERROR,
    message: parsed.message || axiosError?.message || "Unable to consume token."
  };
};

export const consumeTokenLot = async () => {
  const { email, headers } = await getAuthRequestContext();
  const lotNameDate = moment().format("YYYYMMDD_HH:mm:ss");
  const lotName = `${email}_${lotNameDate}`;

  try {
    const resp = await axios.post(
      API_R3API_ODOO_EXECUTE_URI,
      {
        action: "useLot",
        domain: process.env.REACT_APP_DOMAIN_PARAM,
        emailid: email,
        bucketname: process.env.REACT_APP_BASE_BUCKET_NAME,
        token: process.env.REACT_APP_NUMBER_OF_TOKENS_TO_BE_USE,
        filename: lotName
      },
      { headers }
    );

    const httpOk = resp.status >= 200 && resp.status < 300;
    const parsed = parseUseLotResponse(resp.data, { httpOk });

    if (!parsed.ok) {
      throwUseLotFailure(parsed);
    }

    return parsed;
  } catch (error) {
    if (error?.code === USE_LOT_ERROR_CODES.TOKENS_UNAVAILABLE || error?.code === USE_LOT_ERROR_CODES.ERROR) {
      throw error;
    }

    if (error?.response) {
      const parsed = parseUseLotHttpError(error);
      throwUseLotFailure(parsed);
    }

    throw error;
  }
};
